// eslint-disable-next-line no-restricted-imports
import * as amplitude from '@amplitude/analytics-browser'
import * as Sentry from '@sentry/browser'
import debug from 'debug'
import Cookies from 'js-cookie'
import { mapValues, pickBy, update } from 'lodash-es'
import { useCallback, useEffect } from 'react'
import {
  type Location,
  type Params,
  type UIMatch,
  useParams,
} from 'react-router'
import type { JsonValue, ValueOf } from 'type-fest'
import type { TaskSubmitFormValues } from 'src/components/GenerationTask/BaseTaskSubmitForm'
import { LOCALE_COOKIE_KEY } from 'src/i18n/constants'
import type {
  MangaType,
  RefType,
  Task,
} from 'src/services/apollo/generated/graphql'
import { isValuesCnetGenerativeFill } from 'src/services/helpers/artworkApiParamsPacker'
import type { OrderOption } from 'src/services/helpers/orderOptionParams'
import type {
  ExperienceLevel,
  PromptStyle,
} from 'src/services/helpers/preference'
import { bypassAdmin } from 'src/stores/admin'
import type { AppRouteHandle } from 'src/utils/router/route'
import { useLocation, useMatches } from 'src/utils/router/route'
import { getLocalStorage as getSafeLocalStorage } from '~/utils/storage'
import { attemptParseJSON, isServiceWorkerAvailable } from '.'
import { trackAdbi } from './adbiTracker'
import {
  config,
  getAmplitudeApiKey,
  getAmplitudeServerUrl,
  runtimeConfig,
} from './config'
import { DataTracker } from './dataTracker'
import { mutateAllVariants, mutateExperimentParam } from './experimentNew'
import { genUuid } from './genId'
import { useQs } from './qs'
import { setSiftUserId, trackSiftPageView } from './sift'
import { sleep } from './sleep'

const log = debug('pixai:tracker')

// reminder: also change trackerSw.ts

/**
 * Amplitude & tracker initialization (client-only).
 * Guarded for SSR: these SDKs depend on browser APIs.
 */
let ampExpNew: amplitude.Types.BrowserClient | null = null
let dataTracker: DataTracker | null = null

if (typeof window !== 'undefined') {
  amplitude.init(getAmplitudeApiKey(), undefined, {
    serverUrl: getAmplitudeServerUrl(),
    autocapture: {
      pageViews: {
        trackOn: 'attribution',
      },
      webVitals: true,
    },
    appVersion: config.appVersion,
  })

  // Only for experimentNew.ts
  ampExpNew = amplitude.createInstance()
  ampExpNew.init(config.amplitudeApiKey, undefined, {
    serverUrl: config.amplitudeServerUrl,
    defaultTracking: false,
    appVersion: config.appVersion,
    instanceName: 'exp-new',
  })

  dataTracker = new DataTracker(config.getway.ak, config.getway.sk)
}

/**
 * Google tag
 */
declare global {
  interface Window {
    /** Google Tag Manager */
    dataLayer?: Array<
      {
        event?: string
      } & Record<string, any>
    >
  }
}

const gtagLog = debug('pixai:attribution:gtag')

function gtag(..._args: unknown[]) {
  try {
    if (typeof window === 'undefined' || !window.dataLayer) {
      gtagLog('window.dataLayer not available, skipping gtag call:', _args)
      return
    }
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments as any)
  } catch (err) {
    gtagLog('gtag call failed:', err, 'args:', _args)
  }
}

/** Google Ads Enhanced Conversions (via gtag): set first-party user data */
export function reportGtagEnhancedUserData(email: string) {
  const normalizedEmail = email.trim().toLowerCase()
  gtag('set', 'user_data', { email: normalizedEmail })
}

/** Google Ads Enhanced Conversions (via gtag): clear user data */
export function clearGtagEnhancedUserData() {
  gtag('set', 'user_data', { email: '' })
}

export const trackerSetUserId = (
  userId: string,
  options?: { isAdmin?: boolean },
) => {
  if (isServiceWorkerAvailable) {
    navigator.serviceWorker.controller?.postMessage({
      type: 'user-sync',
      userId,
    })
  }

  _adbiUserId = userId
  amplitude.setUserId(userId)
  ampExpNew?.setUserId(userId)
  dataTracker?.setUserId(userId)
  Sentry.setUser({
    id: userId,
  })
  siftDisabledForAdmin = Boolean(options?.isAdmin) && !bypassAdmin()
  if (!siftDisabledForAdmin) {
    setSiftUserId(userId)
  }
  window.dataLayer?.push({
    event: 'set_user_id',
    user_id: userId,
  })
  mutateExperimentParam()
  mutateAllVariants()
}

export const trackerLogout = () => {
  _adbiUserId = null
  Sentry.setUser(null)
  amplitude.setUserId(undefined)
  ampExpNew?.setUserId(undefined)
  dataTracker?.setUserId(undefined)
  siftDisabledForAdmin = false
  setSiftUserId()
  window.dataLayer?.push({
    event: 'set_user_id',
    user_id: null,
  })
  clearGtagEnhancedUserData()
  mutateExperimentParam()
  mutateAllVariants()
}

export type UserProperty = {
  lastPlan: string
  plan: string
  followerCount: number
  followingCount: number
  createdAt: string
  auto_complete_checked: 'null' | boolean
  isAdmin: boolean
  showNsfw: boolean
  blurNsfw: boolean
  ageVerificationStatus: boolean
  creditsLeft: number
  membershipLeft: string
  experienceLevel: ExperienceLevel
  promptStyle: PromptStyle
  user_agent: string
  systemColorPreference: 'dark' | 'light' | 'no-preference'
  language: string
  /** Sticky setOnce properties. */
  initial_platform: 'Web'
  initial_url: string
}

export const trackerSetUserProperties = async (
  properties: Partial<UserProperty> | null,
  setOnceProperties?: Partial<UserProperty>,
) => {
  log('set user properties: %o setOnce: %o', properties, setOnceProperties)
  const identifyEvent = new amplitude.Identify()
  for (const [k, v] of Object.entries(properties ?? {})) {
    if (v !== undefined) {
      identifyEvent.set(k, v)
    }
  }
  for (const [k, v] of Object.entries(setOnceProperties ?? {})) {
    if (v !== undefined) {
      identifyEvent.setOnce(k, v)
    }
  }

  // Identify will flood lots of errors unless apiKey is set
  if (config.amplitudeApiKey) {
    amplitude.identify(identifyEvent)
  }
  if (properties) dataTracker?.setUserProperties(properties)
}

// adbi event forwarding
let _adbiUserId: string | null = null

let lastEvent: string
let siftDisabledForAdmin = false
const STORAGE_KEY_EVENT_GROUP = 'tracker:event-group'
let eventGroups: Record<string, { value: JsonValue; persistent?: boolean }> =
  attemptParseJSON(getSafeLocalStorage(STORAGE_KEY_EVENT_GROUP)) || {}

function readEventGroupValues() {
  return mapValues(eventGroups, value => value.value)
}

export type TrackDestination = 'amplitude' | 'getway' | 'gtm'

export const defaultTrackDestinations = [
  'amplitude',
  'getway',
  'gtm',
] as TrackDestination[]
export interface TrackOptions {
  destinations?: TrackDestination[]
  extraDestinations?: TrackDestination[]
}

export const track = (event: string, attrs?: any, options?: TrackOptions) => {
  lastEvent = event
  attrs = {
    viewportWidth:
      typeof window !== 'undefined' ? window.innerWidth : undefined,
    buildTimestamp: runtimeConfig.buildTimestamp,
    ...attrs,
    ...readEventGroupValues(),
  }
  log('event: %s attrs: %o', event, attrs)

  const destinations = [
    ...(options?.destinations ?? defaultTrackDestinations),
    ...(options?.extraDestinations ?? []),
  ]

  // amplitude events should be sent to logger service if enabled even if it's dev mode.
  // logger service is a platform to debug amplitude events tracking
  // typeof window guard: amplitude.init() is skipped on SSR; calling track()
  // without init queues events that flush against browser APIs and crash.
  if (destinations.includes('amplitude') && typeof window !== 'undefined') {
    amplitude.track(event, attrs)
  }

  trackAdbi(event, _adbiUserId, attrs)

  if (config.isDev) return

  if (destinations.includes('getway')) {
    dataTracker?.track(event, attrs)
  }
  if (destinations.includes('gtm')) {
    window.dataLayer?.push({ event, ...attrs })
  }
}

function writeEventGroups() {
  localStorage.setItem(STORAGE_KEY_EVENT_GROUP, JSON.stringify(eventGroups))
}

/**
 * Start an event group (marks for a series of events)
 *
 * @param name               - The name of an event group.
 * @param value              - The value of the event group.
 * @param options
 * @param options.persistent - Whether not to remove the event group after page reload.
 *                             Event groups are auto-removed during page reloads.
 *                             Set to `true` to keep the event group.
 *                             Persistent event groups also remain after `endEventGroup(true)`.
 *                             You will have to remove them manually.
 */
export function startEventGroup(
  name: string,
  value?: JsonValue,
  options?: { persistent?: boolean },
) {
  eventGroups[name] = { value: value ?? lastEvent, ...options }
  writeEventGroups()
}

export enum UpdateEventGroupAction {
  Increment = 'Increment',
}

export function updateEventGroup(name: string, action: UpdateEventGroupAction) {
  switch (action) {
    case UpdateEventGroupAction.Increment:
      update(eventGroups, name, prev => ({
        ...prev,
        value: (typeof prev?.value === 'number' ? prev.value : 0) + 1,
      }))
      break
  }
  writeEventGroups()
}

/**
 * Close an event group
 *
 * @param name - The name of an event group.
 *               `true` to close all groups that are not persistent.
 */
export function endEventGroup(name: string | true) {
  if (name === true) {
    eventGroups = pickBy(eventGroups, value => value.persistent)
  } else {
    delete eventGroups[name]
  }
  writeEventGroups()
}

type RouteMatch = UIMatch<unknown, AppRouteHandle>
let lastPathname: string | undefined
let lastMatches: RouteMatch[] | undefined

export function trackPageView({
  location,
  matches,
  params,
  data,
}: {
  location: Location
  matches: RouteMatch[]
  params: Params
  data?: Record<string, JsonValue | undefined>
}) {
  trackActionWithPageViewParams({
    name: 'page_view',
    location,
    matches,
    params,
    data,
  })
}

function getLocaleStorage(): 'cookie' | 'localStorage' | 'both' | 'none' {
  if (typeof window === 'undefined') return 'none'
  try {
    const hasCookie = !!Cookies.get(LOCALE_COOKIE_KEY)
    const hasLocalStorage = !!getSafeLocalStorage('i18nextLng')
    if (hasCookie && hasLocalStorage) return 'both'
    if (hasCookie) return 'cookie'
    if (hasLocalStorage) return 'localStorage'
  } catch (e) {
    console.error('[tracker] getLocaleStorage failed', e)
    Sentry.captureException(e)
  }
  return 'none'
}

function buildPageContextProps(
  location: Location,
  matches: RouteMatch[],
  params: Params,
) {
  const ref = new URLSearchParams(location.search).get('ref') ?? undefined
  return {
    ...params,
    pathname: location.pathname,
    routeName: getRouteName(matches),
    ...getRouteTrackingData(matches),
    ref,
    documentWidth: document.documentElement.clientWidth,
    documentHeight: document.documentElement.clientHeight,
    ssr: runtimeConfig.isSSR,
    localeStorage: getLocaleStorage(),
  }
}

export function trackActionWithPageViewParams({
  name,
  location,
  matches,
  params,
  data,
}: {
  name: string
  location: Location
  matches: RouteMatch[]
  params: Params
  data?: Record<string, JsonValue | undefined>
}) {
  track(name, {
    ...buildPageContextProps(location, matches, params),
    fromPathname: lastPathname,
    fromRouteName: getRouteName(lastMatches),
    fromScene: getRouteTrackingData(lastMatches)?.scene,
    ...data,
  })

  lastPathname = location.pathname
  lastMatches = matches
}

export function trackPageRender({
  location,
  matches,
  params,
  pageName,
  extraProps,
}: {
  location: Location
  matches: RouteMatch[]
  params: Params
  pageName: string
  extraProps?: Record<string, unknown>
}) {
  // TODO: page_render is temporarily disabled — the event cannot capture
  // meaningful values but consumes significant tracking budget.
  return

  track('page_render', {
    ...buildPageContextProps(location, matches, params),
    page: pageName,
    render_result: 'success',
    ...extraProps,
  })
}

const getRouteName = (matches?: RouteMatch[]) => {
  return matches?.at(-1)?.handle?.name
}

const getRouteTrackingData = (matches?: RouteMatch[]) => {
  const match = matches?.findLast(it => it.handle?.track)
  const value = match?.handle?.track
  if (value) {
    return typeof value === 'function' ? value(match) : value
  }
}

export function useTrackPageView() {
  const location = useLocation()
  const matches = useMatches() as RouteMatch[]
  const params = useParams()

  return useCallback(
    (data?: Record<string, JsonValue | undefined>) => {
      trackPageView({ location, matches, params, data })
    },
    [location, matches, params],
  )
}

export function useTrackWithPageViewParams(name: string) {
  const location = useLocation()
  const matches = useMatches() as RouteMatch[]
  const params = useParams()

  return useCallback(
    (data?: Record<string, JsonValue | undefined>) => {
      trackActionWithPageViewParams({ name, location, matches, params, data })
    },
    [location, matches, params],
  )
}

export function useAutoTrackPageView() {
  const location = useLocation()
  const matches = useMatches() as RouteMatch[]
  const params = useParams()
  const [qs, setQs] = useQs()

  useEffect(() => {
    if (location.pathname === lastPathname) return

    /**
     * `ref` means coming from opening a notification
     */
    const ref = qs['ref']
    if (ref) {
      if (location.pathname.startsWith('/@')) {
        // HACK: ensure user profile page can redirect to /artwork successfully
        setTimeout(() => {
          setQs({ ref: null })
        }, 200)
      } else {
        setQs({ ref: null })
      }
    }

    trackPageView({ location, matches, params })
    if (!siftDisabledForAdmin) {
      trackSiftPageView(location.pathname)
    }
    // Only run on pathname change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname])
}

export const trackUserPreference = ({
  type,
  action,
  taskId,
  currentTaskId,
  taskParams,
  publishParams,
  otherParams,
}: {
  type:
    | 'variation'
    | 'upscale'
    | 'enlarge'
    | 'publish'
    | 'download'
    | 'click'
    | 'fav'
    | 'delete'
  action?: 'cancel' | 'click' | 'confirm' | 'success'
  taskId?: string
  /**
   * currentTaskId: submitted task's id
   */
  currentTaskId?: string
  taskParams?: TaskSubmitFormValues | Task['parameters']
  // only avaliable when type === 'publish'
  publishParams?: {
    isPrivate?: boolean | null
    hidePrompts?: boolean | null
    tags?: string[] | null
    title?: string | null
    extra?: {
      description?: string
    }
    hasFav?: boolean
  }
  otherParams?: {
    scene?: string
    mediaIdx?: number
    mediaId?: string
    duration?: number
  }
}) => {
  const { scene, mediaIdx, mediaId, duration } = otherParams ?? {}

  const getTaskParams = (t?: TaskSubmitFormValues | Task['parameters']) => {
    const useI2i =
      t.mediaId != null || // Task
      t.baseImage != null // Form Values

    const hasUpscale = (t.upscale ?? 0) > 1
    return {
      batchSize: t.batchSize,
      useI2i,
      useLoRA: t.lora != null && Object.keys(t.lora).length > 0,
      useControlNet:
        !useI2i && t.controlNets != null && t.controlNets.length > 0,
      useHires:
        !useI2i &&
        hasUpscale &&
        (t.upscaleDenoisingStrength != null || t.upscaleDenoisingSteps != null),
      useUpscale: useI2i && t.enlarge > 1,
      useImageEnhance: useI2i && hasUpscale,
      useGenerativeFill: isValuesCnetGenerativeFill(t),
      useAnimate: Boolean(t.animateDiff?.enabled),
      modelName: t.model,
      modelId: t.modelId,
    }
  }

  const getPublishParams = (p: typeof publishParams) => ({
    isPrivate: p?.isPrivate ?? false,
    hidePrompts: p?.hidePrompts ?? false,
    hasTitle: (p?.title != null && p.title.length > 0) ?? false,
    hasTag: (p?.tags ?? []).length > 0,
    hasDescription:
      p?.extra?.description != null && p?.extra.description.length > 0,
  })

  return track(
    'user_preference',
    {
      // normal attrs,
      type,
      scene,
      taskId,
      currentTaskId,
      mediaIdx,
      mediaId,
      action,
      duration,
      // task params,
      ...getTaskParams(taskParams),
      // publish params,
      ...getPublishParams(publishParams),
    },
    {
      destinations: ['getway'],
    },
  )
}

interface AmplitudeQueueItem {
  event: string
  attrs: any
  options?: TrackOptions
}

/**
 * Event sender that batches `reco_artwork_view` events and aggregates them into `reco_artwork_view2`.
 *
 * this class is only a wrapper for `track` function:
 * - Queues `reco_artwork_view` events and processes them in batches
 * - Aggregates multiple events into one `reco_artwork_view2` event with combined metadata
 * - use `track` for all other events
 */
class AmplitudeSender {
  private queue: AmplitudeQueueItem[] = []
  private processRecoViewPromise: Promise<void> | null = null
  private readonly intervalMs = 500
  private readonly batchSize = 8

  send(event: string, attrs: any, options?: TrackOptions) {
    // only create reco_artwork_view2 event from reco_artwork_view, do not track reco_artwork_view
    if (event === 'reco_artwork_view') {
      const queueItem: AmplitudeQueueItem = {
        event,
        attrs,
        options,
      }
      this.queue.push(queueItem)
      // the first one will start the processing and waiting for more items; following ones will only enqueue.
      if (!this.processRecoViewPromise) {
        this.processRecoViewPromise = this.processRecoView()
      }
    } else {
      track(event, attrs, options)
    }
  }

  private async processRecoView(): Promise<void> {
    try {
      // wait for more items to be enqueued
      await sleep(this.intervalMs)

      while (this.queue.length > 0) {
        const batch = this.queue.splice(0, this.batchSize)

        if (batch.length > 0) {
          const attr = batch[0].attrs
          const artworks = batch.map(item => item.attrs)

          track(
            'reco_artwork_view2',
            {
              artworkIds: artworks.map(item => item.artworkId),
              artworkLikeCounts: artworks.map(item => item.currentLikeCount),
              retrieverIds: artworks.map(item => item.retrieverId),
              artworkCount: artworks.length,
              indexBegin: Math.min(...artworks.map(item => item.index)),
              indexEnd: Math.max(...artworks.map(item => item.index)),
              indexAvg:
                artworks.reduce((sum, item) => sum + item.index, 0) /
                artworks.length,
              totalCols: attr.totalCols,
              feed: attr.feed,
              feedSession: attr.feedSession,
              scene: attr.scene,
              pathname: attr.pathname,
            },
            {
              destinations: batch[0].options?.destinations,
            },
          )
        }
      }
    } catch (error) {
      console.error('AmplitudeSender processing error:', error)
    } finally {
      this.processRecoViewPromise = null
    }
  }
}

const amplitudeSender = new AmplitudeSender()

/**
 * Tracking events for recommendations
 */

/**
 * Should be reset at first load/page switching/feed switching
 */
let recoTrackingFeedUuid: string | null = null

type TrackImageForRecArgs =
  | {
      artworkId: string
      /**
       * Index of current artwork
       */
      index: number
      totalCols: number
      currentLikeCount: number

      scene: 'artwork_feed' | 'model_feed'
      feed: ValueOf<typeof OrderOption>
      type?: RefType | MangaType
      likeFrom?: 'grid' | 'detail'

      /**
       * reweighting props
       */
      hidePrompts?: boolean
      retrieverId?: string

      pathname?: string
    }
  | {
      fromFeed: string
      toFeed: string
    }

type TrackImageForRecOpts = {
  regenFeedUuid?: boolean
  /** If provided, use this feedUuid from server instead of generating one */
  feedUuid?: string
}

/**
 * Specialized tracking function for image recommendations
 * related actions
 */
export async function trackImageForRec(
  eventName: string,
  eventArgs: TrackImageForRecArgs,
  opts?: TrackImageForRecOpts,
) {
  try {
    // Use server-provided feedUuid if available, otherwise generate one
    if (opts?.feedUuid) {
      recoTrackingFeedUuid = opts.feedUuid
    } else if (recoTrackingFeedUuid == null || opts?.regenFeedUuid) {
      recoTrackingFeedUuid = await genUuid()
    }
  } catch (e: unknown) {
    // UUID not available
    return
  }

  const atts = {
    ...eventArgs,
    feedSession: recoTrackingFeedUuid,
  }
  const options = {
    destinations: ['getway', 'amplitude'] as TrackDestination[],
  }
  amplitudeSender.send(eventName, atts, options)
}
